// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

// import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
// import MDBadge from "theme/components/MDBadge";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

function ApiIntegrationView() {
  const pageTitle = "API Integrations";
  usePageTitle(pageTitle);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [apiTokenPair, setApiTokenPair] = useState({});
  const [apiLogs, setApiLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [openSelectedLogDialog, setOpenSelectedLogDialog] = useState(false);

  const apiLogsTableColumns = [
    { Header: "#", accessor: "actions", align: "left" },
    { Header: "Endpoint Type", accessor: "type", align: "left" },
    { Header: "Endpoint Url", accessor: "url", align: "left" },
    { Header: "Status Code", accessor: "status", align: "left" },
    { Header: "Successful", accessor: "success", align: "right" },
    { Header: "Time", accessor: "created", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        type: row.endpoint.endpoint_type,
        url: row.endpoint.endpoint_url,
        status: row.response_status,
        created: row.created,
        success: (
          <MDBox>
            <Icon color={row.successful ? "success" : "error"}>
              {row.successful ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        actions: (
          <MDBox mt={1} variant="button">
            <Tooltip title="View Log">
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                iconOnly
                onClick={() => {
                  setSelectedLog(row);
                  setOpenSelectedLogDialog(true);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  visibility
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
        // created: row.created,
      });
      return null;
    });

    return rows;
  };

  async function recyclePair() {
    setShowLoading(true);

    await axios
      .get(`portal/api/integration/recycle.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("portal/api/integration.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setApiTokenPair(result.data.data.token);
            const rows = buildRows(result.data.data.logs);
            setApiLogs(rows);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function resendLog(logRef) {
    setOpenSelectedLogDialog(false);
    setShowLoading(true);

    await axios
      .get(`portal/api/integration/logs/${logRef}/resend.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {selectedLog && (
        <Dialog open={openSelectedLogDialog} fullWidth maxWidth="sm">
          <DialogTitle>Log Details</DialogTitle>
          <DialogContent>
            <MDBox>
              <MDBox mt={2}>
                <MDInput
                  value={selectedLog.endpoint.endpoint_url}
                  multiline
                  margin="dense"
                  label="Endpoint URL"
                  type="text"
                  fullWidth
                  variant="outlined"
                />
              </MDBox>
              <MDBox mt={2}>
                <MDInput
                  value={selectedLog.request_object}
                  multiline
                  margin="dense"
                  label="JSON Payload"
                  type="text"
                  fullWidth
                  variant="outlined"
                />
              </MDBox>
              <MDBox mt={2}>
                <MDInput
                  value={selectedLog.response_status}
                  multiline
                  margin="dense"
                  label="Response Status Code"
                  type="text"
                  fullWidth
                  variant="outlined"
                />
              </MDBox>
              <MDBox mt={2}>
                <MDInput
                  value={selectedLog.response_object}
                  multiline
                  margin="dense"
                  label="Response Object"
                  type="text"
                  fullWidth
                  variant="outlined"
                />
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="outlined"
              color="dark"
              circular
              iconOnly
              onClick={() => setOpenSelectedLogDialog(false)}
            >
              <Icon>close</Icon>
            </MDButton>
            {!selectedLog.successful && (
              <Tooltip title="Re-send">
                <MDButton
                  variant="contained"
                  color="info"
                  circular
                  iconOnly
                  onClick={() => resendLog(selectedLog.reference)}
                >
                  <Icon>send</Icon>
                </MDButton>
              </Tooltip>
            )}
          </DialogActions>
        </Dialog>
      )}

      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox>
                  <Card sx={{ height: "100%", marginTop: "0px" }}>
                    <MDBox p={3}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="h6" fontWeight="medium">
                          API Integration
                        </MDTypography>
                        <MDBox>
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="dark"
                            onClick={() => {
                              const downloadUrl =
                                "https://documenter.getpostman.com/view/242585/2sA35MzK5X";
                              window.open(downloadUrl, "_blank");
                            }}
                          >
                            Documentation
                          </MDButton>
                        </MDBox>
                      </MDBox>
                      <MDBox mt={5}>
                        <MDBox>
                          <MDTypography variant="caption" fontWeight="regular">
                            API Token
                          </MDTypography>
                          <MDInput
                            readonly
                            margin="dense"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={apiTokenPair.key}
                          />
                        </MDBox>
                        <MDBox mt={3}>
                          <MDTypography variant="caption" fontWeight="regular">
                            API Secret
                          </MDTypography>
                          <MDInput
                            readonly
                            margin="dense"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={apiTokenPair.secret}
                          />
                        </MDBox>
                        <MDBox mt={3}>
                          <MDButton variant="contained" color="dark" onClick={() => recyclePair()}>
                            {apiTokenPair.secret !== null ? "Re-Generate" : "Generate"}
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <MDBox mt={5}>
          <Card>
            <MDBox>
              <MDBox p={2}>
                <MDTypography variant="h6">Logs</MDTypography>
              </MDBox>
              <MDBox>
                {apiLogs.length > 0 ? (
                  <MDBox mb={3}>
                    <DataTable
                      table={{ columns: apiLogsTableColumns, rows: apiLogs }}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries
                      noEndBorder
                    />
                  </MDBox>
                ) : (
                  <MDBox p={2}>
                    <MDTypography variant="caption" fontSize={14}>
                      No logs available.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ApiIntegrationView;
